import { useEffect, useRef, useState, createContext, useContext } from 'react';
import axios from 'axios';
import * as Icons from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'hoc_chat_session';

const ChatCtx = createContext(null);

function makeSessionId() {
  return 'hoc_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function ChatProvider({ children }) {
  const [open, setOpen] = useState(false);
  return <ChatCtx.Provider value={{ open, setOpen }}>{children}</ChatCtx.Provider>;
}

export function useChat() {
  const ctx = useContext(ChatCtx);
  return ctx || { open: false, setOpen: () => {} };
}

export function ChatOpenButton({ className = '', label = 'Talk to us', testId = 'chat-open-cta' }) {
  const { setOpen } = useChat();
  return (
    <button type="button" onClick={() => setOpen(true)} className={className} data-testid={testId}>
      {label}
    </button>
  );
}

export default function ChatWidget() {
  const { open, setOpen } = useChat();
  const [sessionId] = useState(() => {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    const sid = makeSessionId();
    localStorage.setItem(STORAGE_KEY, sid);
    return sid;
  });
  const [messages, setMessages] = useState([
    { role: 'assistant', text: "Hi, I'm HearthOak's intake assistant. What kind of work do you need help with?" },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 150);
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: 'assistant', text: res.data.reply }]);
      if (res.data.submitted) setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Chat is temporarily unavailable. Please call us or use the estimate form below.';
      setMessages((m) => [...m, { role: 'assistant', text: msg, error: true }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const reset = async () => {
    try { await axios.post(`${API}/chat/reset`, { session_id: sessionId }); } catch {}
    localStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  };

  return (
    <>
      {/* Floating bubble */}
      <AnimatePresence>
        {!open && (
          <motion.button
            type="button"
            onClick={() => setOpen(true)}
            initial={{ opacity: 0, scale: 0.7, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.7, y: 20 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-[60] bg-brand-maroon text-brand-bone shadow-[0_14px_34px_-14px_rgba(28,28,29,0.55)] hover:bg-brand-maroonDark pl-3.5 pr-4 py-3 md:pl-4 md:pr-5 md:py-3.5 inline-flex items-center gap-2 md:gap-2.5 font-sans text-xs md:text-sm uppercase tracking-[0.12em] rounded-full"
            data-testid="chat-bubble"
            aria-label="Open chat"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-brand-bone/70 animate-ping" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand-bone" />
            </span>
            <Icons.MessagesSquare size={18} strokeWidth={1.8} />
            Talk to us
          </motion.button>
        )}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="scrim"
              className="fixed inset-0 bg-brand-charcoal/40 z-[55]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setOpen(false)}
            />
            <motion.aside
              key="panel"
              initial={{ opacity: 0, y: 30, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="fixed z-[60] right-3 bottom-3 left-3 sm:left-auto sm:right-5 sm:bottom-5 sm:w-[400px] md:w-[420px] max-h-[78vh] sm:max-h-[640px] flex flex-col bg-brand-bone shadow-[0_30px_80px_-20px_rgba(28,28,29,0.5)] border border-brand-border"
              data-testid="chat-panel"
              role="dialog"
              aria-label="Chat with HearthOak"
            >
              {/* Header */}
              <div className="surface-dark px-5 py-4 flex items-center justify-between border-b border-brand-borderDark">
                <div className="flex items-center gap-3">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-brand-maroonBright/60 animate-ping" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand-maroonBright" />
                  </span>
                  <div>
                    <div className="font-serif text-lg text-brand-bone leading-none">HearthOak Chat</div>
                    <div className="font-sans text-[11px] uppercase tracking-[0.18em] text-brand-bone/60 mt-1">Typical reply within 24 hours</div>
                  </div>
                </div>
                <button type="button" onClick={() => setOpen(false)} className="text-brand-bone/70 hover:text-brand-bone" aria-label="Close" data-testid="chat-close">
                  <Icons.X size={20} />
                </button>
              </div>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-brand-bone" data-testid="chat-messages">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] px-3.5 py-2.5 font-sans text-sm leading-relaxed whitespace-pre-wrap ${
                        m.role === 'user'
                          ? 'bg-brand-charcoal text-brand-bone'
                          : m.error
                            ? 'bg-brand-maroon/10 text-brand-charcoal border border-brand-maroon/30'
                            : 'bg-brand-boneAlt text-brand-charcoal border border-brand-border'
                      }`}
                      data-testid={`chat-msg-${m.role}`}
                    >
                      {m.text}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start" data-testid="chat-typing">
                    <div className="bg-brand-boneAlt border border-brand-border px-3.5 py-2.5 inline-flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon animate-bounce [animation-delay:0ms]"></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon animate-bounce [animation-delay:150ms]"></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon animate-bounce [animation-delay:300ms]"></span>
                    </div>
                  </div>
                )}
                {submitted && (
                  <div className="mt-3 border border-brand-maroon/40 bg-brand-maroon/5 px-4 py-3 flex items-start gap-2.5 text-xs text-brand-charcoal font-sans" data-testid="chat-submitted">
                    <Icons.CheckCircle2 size={16} className="text-brand-maroon mt-0.5 flex-shrink-0" />
                    <div>
                      Your details went to HearthOak. The owner will reach out within 24 hours. All pricing is subject to his final approval.
                    </div>
                  </div>
                )}
              </div>

              {/* Input */}
              <div className="border-t border-brand-border p-3 bg-brand-bone">
                <div className="flex items-end gap-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKey}
                    placeholder={submitted ? 'Thanks — the owner will reach out soon.' : 'Type your message…'}
                    rows={1}
                    disabled={submitted}
                    className="flex-1 bg-brand-bone border border-brand-border focus:border-brand-maroon focus:outline-none focus:ring-1 focus:ring-brand-maroon px-3 py-2.5 font-sans text-sm resize-none max-h-24 disabled:opacity-60"
                    data-testid="chat-input"
                  />
                  <button
                    type="button"
                    onClick={send}
                    disabled={sending || !input.trim() || submitted}
                    className="bg-brand-maroon hover:bg-brand-maroonDark text-brand-bone p-2.5 inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Send"
                    data-testid="chat-send"
                  >
                    <Icons.ArrowUp size={18} />
                  </button>
                </div>
                <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-brand-muted">
                  <span>AI assistant · owner approves all quotes</span>
                  <button type="button" onClick={reset} className="hover:text-brand-maroon" data-testid="chat-reset">Start over</button>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
